import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Badge,
  Button,
  Card,
  Col,
  Dropdown,
  Form,
  FormControl,
  InputGroup,
  Modal,
  Pagination,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Edit2,
  Filter,
  Plus,
  RefreshCcw,
  Search,
  Trash2,
  X,
} from "react-feather";
import { toast } from "react-toastify";

import "../../../css/master-common.css";

import {
  clearMastersError,
  fetchMasterThunk,
  createMasterThunk,
  updateMasterThunk,
  deleteMasterThunk,
} from "../../../store/slices/incomeExpenseMastersSlice";

const ITEMS_PER_PAGE = 10;

const SORT_OPTIONS = [
  { value: "name_asc", label: "Name (A-Z)" },
  { value: "name_desc", label: "Name (Z-A)" },
  { value: "status_active_first", label: "Status (Active first)" },
  { value: "status_inactive_first", label: "Status (Inactive first)" },
];

const Pill = ({ children }) => <Badge className="mm-pill">{children}</Badge>;
const StatusPill = ({ status }) => (
  <Pill>{Number(status) === 1 ? "Active" : "Inactive"}</Pill>
);

// ✅ match backend fields
const defaultForm = { expense_type_id: null, expense_type_name: "", status: 1 };

// ✅ Toast confirm helper (no window.confirm)
const confirmToast = (message, { onYes, onNo } = {}) => {
  toast(
    ({ closeToast }) => (
      <div>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>{message}</div>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={() => {
              closeToast?.();
              onYes?.();
            }}
          >
            Delete
          </button>

          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => {
              closeToast?.();
              onNo?.();
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    ),
    {
      autoClose: false,
      closeOnClick: false,
      closeButton: false,
      draggable: false,
    }
  );
};

export default function ExpenseTypeMasterPage() {
  const dispatch = useDispatch();
  const { expenseList, loading, saving, error } = useSelector(
    (s) => s.incomeExpenseMasters
  );

  // modal
  const [showModal, setShowModal] = useState(false);
  const [mode, setMode] = useState("create");
  const [form, setForm] = useState(defaultForm);

  // ui
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("name_asc");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    dispatch(fetchMasterThunk({ type: "expense" }));
  }, [dispatch]);

  // ✅ toast errors (no Alert UI)
  useEffect(() => {
    if (!error) return;
    toast.error(String(error));
    dispatch(clearMastersError());
  }, [error, dispatch]);

  const sortLabel = useMemo(
    () => SORT_OPTIONS.find((x) => x.value === sortBy)?.label || "Sort",
    [sortBy]
  );

  const rows = useMemo(() => {
    const term = String(q || "").trim().toLowerCase();
    let r = Array.isArray(expenseList) ? [...expenseList] : [];

    // ✅ search by expense_type_name
    if (term) {
      r = r.filter((x) =>
        String(x.expense_type_name || "").toLowerCase().includes(term)
      );
    }

    r.sort((a, b) => {
      const aName = String(a.expense_type_name || "");
      const bName = String(b.expense_type_name || "");
      const aSt = Number(a.status) === 1 ? 1 : 0;
      const bSt = Number(b.status) === 1 ? 1 : 0;

      switch (sortBy) {
        case "name_asc":
          return aName.localeCompare(bName);
        case "name_desc":
          return bName.localeCompare(aName);
        case "status_active_first":
          return bSt - aSt;
        case "status_inactive_first":
          return aSt - bSt;
        default:
          return aName.localeCompare(bName);
      }
    });

    return r;
  }, [expenseList, q, sortBy]);

  const totalPages = Math.ceil(rows.length / ITEMS_PER_PAGE) || 1;
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginated = rows.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  useEffect(() => setCurrentPage(1), [q, sortBy]);

  const getSortIcon = (col) => {
    if (sortBy === `${col}_asc`) return <ChevronUp size={16} />;
    if (sortBy === `${col}_desc`) return <ChevronDown size={16} />;
    return null;
  };

  const getPaginationItems = () => {
    if (totalPages <= 1) return null;
    const items = [];
    const isMobile = typeof window !== "undefined" && window.innerWidth < 576;
    const maxVisiblePages = isMobile ? 3 : 5;
    const goTo = (p) => setCurrentPage(Math.min(Math.max(1, p), totalPages));

    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => goTo(currentPage - 1)}
        disabled={currentPage === 1}
      >
        <ChevronLeft size={16} className="me-1" />
        Prev
      </Pagination.Prev>
    );

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    if (endPage - startPage + 1 < maxVisiblePages)
      startPage = Math.max(1, endPage - maxVisiblePages + 1);

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => goTo(1)}>
          1
        </Pagination.Item>
      );
      if (!isMobile && startPage > 2)
        items.push(<Pagination.Ellipsis key="es" />);
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === currentPage}
          onClick={() => goTo(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      if (!isMobile && endPage < totalPages - 1)
        items.push(<Pagination.Ellipsis key="ee" />);
      items.push(
        <Pagination.Item key={totalPages} onClick={() => goTo(totalPages)}>
          {totalPages}
        </Pagination.Item>
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        onClick={() => goTo(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        Next
        <ChevronRight size={16} className="ms-1" />
      </Pagination.Next>
    );

    return items;
  };

  const openCreate = () => {
    setMode("create");
    setForm({ ...defaultForm });
    setShowModal(true);
    dispatch(clearMastersError());
  };

  const openEdit = (r) => {
    setMode("edit");
    setForm({
      expense_type_id: r.expense_type_id,
      expense_type_name: r.expense_type_name || "",
      status: Number(r.status) === 1 ? 1 : 0,
    });
    setShowModal(true);
    dispatch(clearMastersError());
  };

  const closeModal = () => setShowModal(false);

  const onSubmit = async (ev) => {
    ev.preventDefault();

    const expense_type_name = String(form.expense_type_name || "").trim();
    const status = Number(form.status) === 1 ? 1 : 0;

    if (!expense_type_name) return toast.warning("Expense Type name required");

    const payload = { expense_type_name, status };

    if (mode === "create") {
      const r = await dispatch(createMasterThunk({ type: "expense", payload }));
      if (createMasterThunk.fulfilled.match(r)) {
        toast.success("Expense Type created ✅");
        setShowModal(false);
        setQ("");
      } else {
        const msg = r?.payload?.message || r?.payload || r?.error?.message || "Create failed";
        toast.error(String(msg));
      }
    } else {
      const r = await dispatch(
        updateMasterThunk({
          type: "expense",
          id: form.expense_type_id,
          payload,
        })
      );

      if (updateMasterThunk.fulfilled.match(r)) {
        toast.success("Expense Type updated ✅");
        setShowModal(false);
      } else {
        const msg = r?.payload?.message || r?.payload || r?.error?.message || "Update failed";
        toast.error(String(msg));
      }
    }
  };

  const doDelete = async (r) => {
    const x = await dispatch(
      deleteMasterThunk({
        type: "expense",
        id: r.expense_type_id,
      })
    );

    if (deleteMasterThunk.fulfilled.match(x)) toast.success("Deleted 🗑️");
    else {
      const msg = x?.payload?.message || x?.payload || x?.error?.message || "Delete failed";
      toast.error(String(msg));
    }
  };

  const onDelete = (r) => {
    confirmToast(`Delete "${r.expense_type_name}"?`, {
      onYes: () => doDelete(r),
    });
  };

  const clearSearch = () => setQ("");

  return (
    <div className="container-fluid px-3 py-4 mm-page">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-1 fw-bold mm-title">Expense Type Master</h4>
          {/* <div className="mm-subtitle">Create / Edit / Delete Expense Types</div> */}
        </div>

        <div className="d-flex gap-2">
          <Button
            variant="light"
            className="mm-cancel-btn d-flex align-items-center gap-2"
            onClick={() => dispatch(fetchMasterThunk({ type: "expense" }))}
            disabled={loading}
          >
            <RefreshCcw size={16} />
            {loading ? "Refreshing..." : "Refresh"}
          </Button>

          <Button
            onClick={openCreate}
            className="mm-primary-btn d-flex align-items-center gap-2"
          >
            <Plus size={18} />
            Add Expense Type
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mm-card-soft mb-3">
        <Card.Body className="p-3">
          <Row className="g-3 align-items-end">
            <Col md={8}>
              <Form.Label className="fw-bold mb-1">Search</Form.Label>
              <InputGroup>
                <InputGroup.Text className="mm-inputgroup-text">
                  <Search size={18} className="text-muted" />
                </InputGroup.Text>
                <FormControl
                  className="mm-input mm-inputgroup-control"
                  placeholder="Search expense type..."
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
                {q ? (
                  <Button variant="light" className="mm-clear-btn" onClick={clearSearch}>
                    <X size={16} />
                  </Button>
                ) : null}
              </InputGroup>
            </Col>

            <Col md={4}>
              <Form.Label className="fw-bold mb-1">Sort</Form.Label>
              <Dropdown align="end">
                <Dropdown.Toggle className="mm-dropdown-btn w-100 d-flex align-items-center justify-content-between">
                  <span className="d-flex align-items-center gap-2">
                    <Filter size={16} />
                    {sortLabel}
                  </span>
                  <ChevronDown size={16} />
                </Dropdown.Toggle>

                <Dropdown.Menu className="mm-dd-menu w-100">
                  {SORT_OPTIONS.map((opt) => (
                    <Dropdown.Item
                      key={opt.value}
                      active={sortBy === opt.value}
                      onClick={() => setSortBy(opt.value)}
                    >
                      {opt.label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </Col>

            <Col md={12} className="text-end">
              <div className="text-muted small">
                Showing{" "}
                <span className="fw-bold">
                  {rows.length === 0
                    ? 0
                    : startIndex + 1}-{Math.min(startIndex + ITEMS_PER_PAGE, rows.length)}
                </span>{" "}
                of <span className="fw-bold">{rows.length}</span>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Table */}
      <Card className="mm-card">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <div className="mt-2 text-muted">Loading...</div>
            </div>
          ) : (
            <>
              <div className="mm-table-wrap">
                <Table hover className="mm-table mb-0">
                  <thead>
                    <tr>
                      <th
                        style={{ cursor: "pointer" }}
                        onClick={() =>
                          setSortBy((p) => (p === "name_asc" ? "name_desc" : "name_asc"))
                        }
                      >
                        <div className="d-flex align-items-center gap-1">
                          Expense Type {getSortIcon("name")}
                        </div>
                      </th>
                      <th style={{ width: 160, textAlign: "center" }}>Status</th>
                      <th style={{ width: 180, textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginated.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="p-5 text-center">
                          <div className="fw-bold mb-1">
                            {q ? "No results found" : "No expense types found"}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      paginated.map((r) => (
                        <tr key={r.expense_type_id}>
                          <td className="fw-bold" title={r.expense_type_name}>
                            {r.expense_type_name}
                          </td>
                          <td className="text-center">
                            <StatusPill status={r.status} />
                          </td>
                          <td style={{ textAlign: "right" }}>
                            <div className="d-inline-flex gap-2">
                              <Button
                                variant="light"
                                className="mm-icon-btn primary"
                                onClick={() => openEdit(r)}
                                disabled={saving}
                                title="Edit"
                              >
                                <Edit2 size={16} />
                              </Button>
                              <Button
                                variant="light"
                                className="mm-icon-btn danger"
                                onClick={() => onDelete(r)}
                                disabled={saving}
                                title="Delete"
                              >
                                <Trash2 size={16} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              {totalPages > 1 ? (
                <div className="d-flex justify-content-center py-3 border-top mm-footer">
                  <Pagination className="mb-0 master-pagination">{getPaginationItems()}</Pagination>
                </div>
              ) : null}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Modal */}
      <Modal show={showModal} onHide={closeModal} centered className="mm-modal">
        <Form onSubmit={onSubmit}>
          <Modal.Header closeButton>
            <Modal.Title className="fw-bold">
              {mode === "create" ? "Add Expense Type" : "Edit Expense Type"}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <Row className="g-3">
              <Col md={12}>
                <Form.Label className="fw-bold">Expense Type Name *</Form.Label>
                <Form.Control
                  className="mm-input"
                  value={form.expense_type_name}
                  onChange={(e) => setForm((p) => ({ ...p, expense_type_name: e.target.value }))}
                  placeholder="Enter expense type"
                  autoFocus
                />
              </Col>

              <Col md={12}>
                <Form.Label className="fw-bold">Status</Form.Label>
                <Form.Select
                  className="mm-select"
                  value={Number(form.status) === 1 ? 1 : 0}
                  onChange={(e) => setForm((p) => ({ ...p, status: Number(e.target.value) }))}
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </Form.Select>
              </Col>
            </Row>
          </Modal.Body>

          <Modal.Footer>
            <Button
              variant="outline-secondary"
              className="mm-cancel-btn"
              onClick={closeModal}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="mm-primary-btn"
              disabled={saving || !String(form.expense_type_name || "").trim()}
            >
              {saving ? "Saving..." : mode === "create" ? "Save" : "Update"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}